import React, { createRef, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { addConnection, addObstacle, Connection, deleteConnection, deleteObstacle, Obstacle, Point, Port, RootState, RoutingResult, setInputConfiguration, updateChannels, updateConnection, updateObstacle } from ".."
import { ConnectionSVGViewer } from "./ConnectionSVGViewer"
import { ObstacleSVGViewer } from "./ObstacleSVGViewer"
import produce from 'immer'
import { Button, Col, Container, Form, InputGroup, Modal, Row } from "react-bootstrap"
import { Barrier } from "../Utils/Barrier"
import RBush from "rbush"
import { Geometry } from "../Utils/Geometry"
import { ConnectionFormEditor } from "../Geometry/ConnectionFormEditor"
import { ObstacleFormEditor } from "../Geometry/ObstacleFormEditor"
import { V, Vector } from "../Utils/Vector"
import { AiOutlineZoomIn, AiOutlineZoomOut } from "react-icons/ai"
import { MdCenterFocusStrong, MdClose, MdDone } from "react-icons/md"
import { RiDeleteBin2Line, RiRouteLine, RiShapeLine } from "react-icons/ri"
import { PortSVGShape } from "./PortSVGViewer"
import { shades } from "../Utils/Color"
import { ConnectionDetails } from "../Detail/ConnectionDetails"
import { ObstacleDetails } from "../Detail/ObstacleDetails"
import { Channel as RouterChannel, ChannelPath as RouterChannelPath, Connection as RouterConnection, Obstacle as RouterObstacle, Port as RouterPort, Router } from 'mfrouting'
import { Download } from "../Detail/Download"
import { transform, applyToPoint, translate, scale } from 'transformation-matrix';
import { useGesture } from '@use-gesture/react'
import { DownloadDXF } from "../Detail/DownloadDXF"

export { SVGEditor }

export type View = {
  pointerPosition: [number, number] | undefined
  scale: number
  center: [number, number]
  size: {
    width: number
    height: number
  }
  viewBox: {
    minx: number
    miny: number
    width: number
    height: number
  }
}

export type Selection = {
  selected: string
  setSelected: (object: string) => void
}

export type SVGEditorGlobals = {
  view: View
  viewToSVGCoordinate: (point: [number, number]) => [number, number]
  SVGToViewCoordinate: (point: [number, number]) => [number, number]
  barriers: RBush<Barrier>
  selection: Selection
}


function clientToSVGView(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  },
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }
) {
  return transform(
    translate(svgSpace.center[0], svgSpace.center[1]),
    scale(1 / svgSpace.zoomFactor),
    translate(-0.5 * clientSpace.width, -0.5 * clientSpace.height),
  )
}

function applyZoom(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  },
  setSvgSpace: React.Dispatch<React.SetStateAction<{
    center: [number, number];
    zoomFactor: number;
  }>>, 
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }, 
  zoomCenter: [number, number], 
  zoomFactor: number
) {
  const matrix = transform(
    translate(zoomCenter[0], zoomCenter[1]),
    scale(1 / zoomFactor),
    translate(-zoomCenter[0], -zoomCenter[1]),
  )

  const center = applyToPoint(matrix, svgSpace.center)

  setSvgSpace({
    center,
    zoomFactor: svgSpace.zoomFactor * zoomFactor
  })
}

function setZoom(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  }, 
  setSvgSpace: React.Dispatch<React.SetStateAction<{
    center: [number, number];
    zoomFactor: number;
  }>>, 
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }, 
  zoomCenter: [number, number], 
  zoomFactor: number
) {
  const zoom = svgSpace.zoomFactor
  const newZoom = zoomFactor / zoom

  const matrix = transform(
    translate(zoomCenter[0], zoomCenter[1]),
    scale(1 / newZoom),
    translate(-zoomCenter[0], -zoomCenter[1]),
  )

  const center = applyToPoint(matrix, svgSpace.center)

  setSvgSpace({
    center,
    zoomFactor: zoomFactor
  })
}

function moveTo(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  }, 
  setSvgSpace: React.Dispatch<React.SetStateAction<{
    center: [number, number];
    zoomFactor: number;
  }>>, 
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }, 
  center: [number, number]
) {
  setSvgSpace({
    center,
    zoomFactor: svgSpace.zoomFactor
  })
}

function fitToBounds(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  }, 
  setSvgSpace: React.Dispatch<React.SetStateAction<{
    center: [number, number];
    zoomFactor: number;
  }>>, 
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }, 
  leftLower: [number, number], 
  rightUpper: [number, number]
) {
  if(isNaN(leftLower[0]) || isNaN(leftLower[1]) || isNaN(rightUpper[0]) || isNaN(rightUpper[1])) {
    return
  }

  const xdiff = Math.abs(leftLower[0] - rightUpper[0])
  const xmin = Math.min(leftLower[0], rightUpper[0])
  const ydiff = Math.abs(leftLower[1] - rightUpper[1])
  const ymin = Math.min(leftLower[1], rightUpper[1])
  const zoomFactor = Math.min(clientSpace.width / xdiff, clientSpace.height / ydiff)
  const center: [number, number] = [xmin + xdiff / 2, ymin + ydiff / 2]
  
  setSvgSpace({
    center,
    zoomFactor
  })
}

function computeViewBoxTransformation(
  svgSpace: {
    center: [number, number],
    zoomFactor: number
  },
  clientSpace: {
    x: number,
    y: number,
    width: number,
    height: number
  }
) {
  const matrix = clientToSVGView(svgSpace, clientSpace)
  const [viewBoxOriginX, viewBoxOriginY] = applyToPoint(matrix, [0, 0])
  const [viewCornerX, viewCornerY] = applyToPoint(matrix, [clientSpace.width, clientSpace.height])
  const [viewBoxWidth, viewBoxHeight] = [viewCornerX - viewBoxOriginX, viewCornerY - viewBoxOriginY]
  const viewBox = `${viewBoxOriginX} ${viewBoxOriginY} ${viewBoxWidth} ${viewBoxHeight}`

  return { matrix, viewBox }
}

function SVGEditor(): JSX.Element {
  const ZOOM_FACTOR = 0.2
  const MAX_DELTA = 400
  const inputConfiguration = useSelector((state: RootState) => state.inputConfiguration)
  const dispatch = useDispatch()
  //const svg = createRef<SVGSVGElement>();
  /*const [view, setView] = useState({
    initialized: false,
    pointerPosition: undefined,
    scale: 1,
    center: [0, 0],
    size: {
      width: 0,
      height: 0
    },
    viewBox: {
      minx: 0,
      miny: 0,
      width: 0,
      height:0
    }
  } as View)*/

  const [svgSpace, setSvgSpace] = useState({
    center: [0, 0],
    zoomFactor: 1
  } as { center: [number, number], zoomFactor: number })

  const [clientSpace, setClientSpace] = useState({
    x: 0,
    y: 0,
    width: 100,
    height: 100
  } as { x: number, y: number, width: number, height: number })

  const [pointer, setPointer] = useState({
    position: undefined
  } as { position: undefined | [number, number] })

  const view: View = {
    pointerPosition: pointer.position,
    scale: svgSpace.zoomFactor,
    center: svgSpace.center,
    size: {
      width: clientSpace.width,
      height: clientSpace.height
    },
    viewBox: {
      minx: 0,
      miny: 0,
      width: 0,
      height:0
    }
  }

  const transformation = computeViewBoxTransformation.bind(null, svgSpace, clientSpace)
  const zoom = applyZoom.bind(null, svgSpace, setSvgSpace, clientSpace)
  const toZoom = setZoom.bind(null, svgSpace, setSvgSpace, clientSpace)
  const toCenter = moveTo.bind(null, svgSpace, setSvgSpace, clientSpace)
  const fit = fitToBounds.bind(null, svgSpace, setSvgSpace, clientSpace)

  let bounds = [[Math.min(...inputConfiguration.obstacles.flatMap(o => o.points.map(p => p[0]), ...inputConfiguration.connections.flatMap(c => [c.ports[0].position[0], c.ports[1].position[0]]))), 
    Math.min(...inputConfiguration.obstacles.flatMap(o => o.points.map(p => p[1])), ...inputConfiguration.connections.flatMap(c => [c.ports[0].position[1], c.ports[1].position[1]]))], 
    [Math.max(...inputConfiguration.obstacles.flatMap(o => o.points.map(p => p[0])), ...inputConfiguration.connections.flatMap(c => [c.ports[0].position[0], c.ports[1].position[0]])),
    Math.max(...inputConfiguration.obstacles.flatMap(o => o.points.map(p => p[1])), ...inputConfiguration.connections.flatMap(c => [c.ports[0].position[1], c.ports[1].position[1]]))
  ]] as [[number, number], [number, number]]

  const boundsSpan = [Math.abs(bounds[0][0] - bounds[1][0]), Math.abs(bounds[0][1] - bounds[1][1])]
  const f = 0.05
  bounds = [[bounds[0][0] - f * boundsSpan[0], bounds[0][1] - f * boundsSpan[1]],
    [bounds[1][0] + f * boundsSpan[0], bounds[1][1] + f * boundsSpan[1]]]

  const zoomToBounds = () => {
    fit(bounds[0], bounds[1])
  }

  const svg = useRef<SVGSVGElement>(null)

  useEffect(() => {
    if(svg !== undefined && svg.current !== null) {
      setClientSpace(svg.current.getBoundingClientRect())
    }

    zoomToBounds()

    window.addEventListener('resize', () => {
      if(svg !== undefined && svg.current !== null) {
        setClientSpace(svg.current.getBoundingClientRect())
      }
    })

    window.addEventListener('scroll', () => {
      if(svg !== undefined && svg.current !== null) {
        setClientSpace(svg.current.getBoundingClientRect())
      }
    })
  }, [svg.current])

  useEffect(() => {
    svg.current?.addEventListener('wheel', e => {
      e.preventDefault()
    })
  })

  const { matrix, viewBox } = transformation()
  const scrollFactor = 0.1
  const maxDelta = 40

  const gesture = useGesture({
    onWheel: state => {
      if(pointer.position) {
        zoom(applyToPoint(matrix, pointer.position), 1 + scrollFactor * Math.min(state.delta[1], maxDelta) / maxDelta)
      }
    },
    onPinch: state => {
      if(state.intentional) {
        if(!state.first) {
          toZoom(applyToPoint(matrix, state.origin), state.offset[0])
        }
      }
    },
    onMove: state => {
      const { x, y } = clientSpace
      setPointer({
        position: [state.xy[0] - x, state.xy[1] - y]
      })
    },
    onDrag: state => {
      if(state.intentional) {
        if(!state.first) {
          const zero = applyToPoint(matrix, [0, 0])
          const offset = applyToPoint(matrix, state.delta)
          const newCenter = [svgSpace.center[0] - offset[0] + zero[0], svgSpace.center[1] - offset[1] + zero[1]] as [number, number]
          toCenter(newCenter)
        }
      }
    }
  }, {})

  const [selectedObject, setSelectedObject] = useState('' as string)

  function viewToSVGCoordinate(clientPoint: Point): Point {
    if(svg.current === undefined || svg.current === null) {
      throw new Error('SVG not found')
    }

    const point = svg.current.createSVGPoint();
    point.x = clientPoint[0]
    point.y = clientPoint[1]
    const result = point.matrixTransform(svg.current.getScreenCTM()?.inverse())
    return [result.x, result.y]
  }

  function SVGToViewCoordinate(clientPoint: [number, number]): [number, number] {
    if(svg.current === undefined || svg.current === null) {
      throw new Error('SVG not found')
    }

    const point = svg.current.createSVGPoint();
    point.x = clientPoint[0]
    point.y = clientPoint[1]
    const matrix = svg.current.getScreenCTM()

    if(matrix === null) {
      throw new Error('SVG not found')
    }

    const result = point.matrixTransform(matrix)
    return [result.x, result.y]
  }

  const barriers = new RBush<Barrier>()
  inputConfiguration.obstacles.forEach(o => {
    if(o.points.length < 3) {
      return
    }
    const polygon = Geometry.polygonIsClockwise(o.points) ? o.points : [...o.points].reverse()
    const transformed = polygon.reduce((a: { point: [number, number], barriers: Barrier[] }, c: [number, number]) => {
      a.barriers.push(new Barrier({ points: [a.point, c], normal: Geometry.leftOrthogonal([c[0] - a.point[0], c[1] - a.point[1]]) }))
      a.point = c
      return a
    }, { point: polygon[polygon.length - 1], barriers: [] })
    barriers.load(transformed.barriers)
  })

  const globals = {
    view,
    viewToSVGCoordinate,
    SVGToViewCoordinate,
    barriers,
    selection: {
      selected: selectedObject,
      setSelected: (object: string) => setSelectedObject(object)
    } as Selection,
    deletion: {}
  }

  /*function updatePointerPosition(position: [number, number] | undefined) {
    if(position === undefined) {
      setView(produce(view, (draft) => {
        draft.pointerPosition = undefined
      }))
    } else {
      const coord = viewToSVGCoordinate(position)
      if(coord !== undefined) {
        setView(produce(view, (draft) => {
          draft.pointerPosition = coord
        }))
      }
    }
  }*/

  /*function zoom(point: [number, number], scale: number) {
    setView(produce(view, (draft) => {
      if(svg.current !== undefined && svg.current !== null) {
        const inverseScale = 1 / scale
        const matrix = (new DOMMatrix()).preMultiplySelf((new DOMMatrixReadOnly()).translate(-point[0], -point[1]))
        matrix.preMultiplySelf((new DOMMatrixReadOnly()).scale(inverseScale, inverseScale))
        matrix.preMultiplySelf((new DOMMatrixReadOnly()).translate(point[0], point[1]))
        const corner = new DOMPoint(draft.viewBox.minx, draft.viewBox.miny)
        const n = corner.matrixTransform(matrix)
        draft.viewBox.minx = n.x
        draft.viewBox.miny = n.y
        draft.viewBox.width *= inverseScale
        draft.viewBox.height *= inverseScale
        draft.center[0] = draft.viewBox.minx + 0.5 * draft.viewBox.width
        draft.center[1] = draft.viewBox.miny + 0.5 * draft.viewBox.height
        draft.scale = draft.size.width / draft.viewBox.width
      }
    }))
  }*/

  function zoomIn() {
    zoom(view.center, 1+ZOOM_FACTOR)
  }

  function zoomOut() {
    zoom(view.center, 1/(1+ZOOM_FACTOR))
  }

  /*function scroll(e: React.WheelEvent<SVGSVGElement>) {
    if(view.pointerPosition !== undefined) {
      zoom(view.pointerPosition, 1 + ZOOM_FACTOR * Math.min(e.nativeEvent.deltaY, MAX_DELTA) / MAX_DELTA)
    }
  }*/

  const [newObstacleEditor, setNewObstacleEditor] = useState({
    active: false,
    points: [] as Point[],
    preview: undefined as Point | undefined,
    previewIsOnFirst: false
  })

  const [newConnectionEditor, setNewConnectionEditor] = useState({
    active: false,
    settings: {},
    settingsDone: false,
    ports: [],
    firstPortDone: false
  } as {
    active: boolean
    settings: {
      width?: number
      spacing?: number
      minBendRadius?: number
    }
    settingsDone: boolean
    ports: Port[]
    firstPortDone: boolean
  })

  function clickSVG(e: React.PointerEvent<SVGSVGElement>) {
    const clickCoordinates: Point = [e.clientX, e.clientY]
    if(newObstacleEditor.active) {
      if(newObstacleEditor.points.length > 0 && V.distance(clickCoordinates, SVGToViewCoordinate(newObstacleEditor.points[0])) <= settings.firstInteractionPointRadius) {
        dispatch(addObstacle(newObstacleEditor.points))
        setNewObstacleEditor({
          active: false,
          points: [],
          preview: undefined,
          previewIsOnFirst: false
        })
      } else {
        setNewObstacleEditor({
          active: true,
          points: [...newObstacleEditor.points, viewToSVGCoordinate(clickCoordinates)],
          preview: undefined,
          previewIsOnFirst: false
        })
      }

      e.stopPropagation()
    } else if(newConnectionEditor.active && newConnectionEditor.settingsDone && newConnectionEditor.ports.length > 0) {
      if(!newConnectionEditor.firstPortDone) {
        setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
          const position = viewToSVGCoordinate(clickCoordinates)
          const port = {
            position,
            direction: [1, 0] as Vector
          }

          draft.ports =[Geometry.findMovedPort(port, position, settings.interactionPointRadius / globals.view.scale, barriers), Geometry.findMovedPort(port, position, settings.interactionPointRadius / globals.view.scale, barriers)]
          draft.firstPortDone = true
        }))
      } else {
        setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
          const position = viewToSVGCoordinate(clickCoordinates)
          const port = {
            position,
            direction: [-1, 0] as Vector
          }
          const result = Geometry.findMovedPort(port, position, settings.interactionPointRadius / globals.view.scale, barriers)
          draft.ports.splice(1, 1, result)
        }))

        dispatch(addConnection({
          ports: newConnectionEditor.ports as [Port, Port],
          width: newConnectionEditor.settings.width!,
          spacing: newConnectionEditor.settings.spacing!,
          minBendRadius: newConnectionEditor.settings.minBendRadius!,
          modifyLength: false,
          length: undefined
        }))

        setNewConnectionEditor({
          active: false,
          settings: {},
          settingsDone: false,
          ports: [],
          firstPortDone: false,
        })
      }
    }
  }

  function rightClickSVG(e: React.PointerEvent<SVGSVGElement>) {
    if(newObstacleEditor.active) {
      setNewObstacleEditor({
        active: newObstacleEditor.points.length !== 1,
        points: [...newObstacleEditor.points.slice(0, -1)],
        preview: undefined,
        previewIsOnFirst: false
      })
      e.stopPropagation()
    }
    e.preventDefault()
  }

  function pointerMoveSVG(e: React.PointerEvent<SVGSVGElement>) {
    const coords: Point = [e.clientX, e.clientY]
    //updatePointerPosition(coords)

    if(newObstacleEditor.active) {
      setNewObstacleEditor(produce(newObstacleEditor, (draft) => {
        draft.preview = viewToSVGCoordinate(coords)
      }))
      e.stopPropagation()
    } else if(newConnectionEditor.active && newConnectionEditor.settingsDone) {
      if(newConnectionEditor.ports.length === 0 || newConnectionEditor.ports.length === 1) {
        setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
          const position = viewToSVGCoordinate(coords)
          const port = {
            position,
            direction: [1, 0] as Vector
          }

          draft.ports.splice(0, newConnectionEditor.ports.length === 0 ? 0 : 1, Geometry.findMovedPort(port, position, settings.interactionPointRadius / globals.view.scale, barriers))
        }))
      } else if(newConnectionEditor.firstPortDone) {
        setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
          const position = viewToSVGCoordinate(coords)
          const port = {
            position,
            direction: [-1, 0] as Vector
          }

          draft.ports.splice(1, 1, Geometry.findMovedPort(port, position, settings.interactionPointRadius / globals.view.scale, barriers))
        }))
      }
      e.stopPropagation()
    }
  }

  function pointerOutSVG(e: React.PointerEvent<SVGSVGElement>) {
    //updatePointerPosition(undefined)
  }

  useEffect(() => {
    svg.current?.addEventListener('wheel', e => {
      e.preventDefault()
    })
  })

  /*useEffect(() => {
    setView(produce(view, (draft) => {
      if(!draft.initialized) {
        if(svg.current !== undefined && svg.current !== null) {
          draft.size.width = svg.current.clientWidth
          draft.size.height = svg.current.clientHeight
          draft.viewBox.minx = svg.current.clientLeft
          draft.viewBox.miny = svg.current.clientTop
          draft.viewBox.width = svg.current.clientWidth
          draft.viewBox.height = svg.current.clientHeight
          draft.initialized = true
          draft.center[0] = draft.viewBox.minx + 0.5 * draft.viewBox.width
          draft.center[1] = draft.viewBox.miny + 0.5 * draft.viewBox.height
        }
      }
    }))
  }, [svg]);*/

  let s: Obstacle | Connection | undefined = undefined
  let t = ''
  const connection = inputConfiguration.connections.find(c => c.id === globals.selection.selected)
  const obstacle = inputConfiguration.obstacles.find(o => o.id === globals.selection.selected)
  if(connection !== undefined) {
    s = connection
    t = 'Connection'
  } else if(obstacle !== undefined) {
    s = obstacle
    t = 'Obstacle'
  } else {
    s = undefined
    t = 'None'
  }

  const settings = useSelector((state: RootState) => state.settings)

  /*
  const route = async () => {
    console.log('Routing...')
    const connections = inputConfiguration.connections.map(c => new RouterConnection({
      id: c.id,
      ports: [
        new RouterPort({
          ...c.ports[0]
        }),
        new RouterPort({
          ...c.ports[1]
        })
      ],
      width: c.width,
      spacing: c.spacing,
      minBendradius: c.minBendRadius,
      modifyLength: c.modifyLength,
      length: c.length
    }))
    const obstacles = inputConfiguration.obstacles.map(o => new RouterObstacle({
      points: o.points
    }))
    try {
      const t0 = performance.now()
      const results = Router.route({ connections, obstacles })
      const t1 = performance.now()
      console.log((t1-t0) + 'ms')
      const routingResults: Record<string, RoutingResult> = {}
      results.forEach((result, id) => {
        const channel = result.channel
        if(!result.routingFound || channel === undefined) {
          routingResults[id] = {
            connection: result.connection.id,
            routingFound: false,
            routingWithLengthFound: false,
            channel: undefined
          }
          return
        }

        routingResults[id] = {
      connection: result.connection.id,
      routingFound: result.routingFound,
      routingWithLengthFound: result.routingWithLengthFound,
      channel: {
          path: {
            startPoint: channel.path.startPoint,
            endPoint: channel.path.endPoint,
            pathString: channel.path.pathString,
            length: channel.path.length,
            arcs: channel.path.arcs.map(a => {
              return {
                startPoint: a.startPoint,
                startDirection: a.startDirection,
                endPoint: a.endPoint,
                endDirection: a.endDirection,
                center: a.center,
                radius: a.radius,
                rotation: a.rotation,
                length: a.length
              }
            })
          },
          width: channel.width,
          length: channel.length
        }
      }
      })
      dispatch(updateChannels(routingResults))
    } catch(e) {
      dispatch(updateChannels({}))
      console.log(e)
    }
  }*/

  const routeAndExtend = async () => {
    console.log('Extended Routing...')
    const connections = inputConfiguration.connections.map(c => new RouterConnection({
      id: c.id,
      ports: [
        new RouterPort({
          ...c.ports[0]
        }),
        new RouterPort({
          ...c.ports[1]
        })
      ],
      width: c.width,
      spacing: c.spacing,
      minBendradius: c.minBendRadius,
      modifyLength: c.modifyLength,
      length: c.length
    }))
    const obstacles = inputConfiguration.obstacles.map(o => new RouterObstacle({
      points: o.points
    }))
    try {
      const t0 = performance.now()
      const results = Router.routeAndExtend({ connections, obstacles })
      const t1 = performance.now()
      console.log((t1-t0) + 'ms')
      const routingResults: Record<string, RoutingResult> = {}
      results.forEach((result, id) => {
        const channel = result.channel
        if(!result.routingFound || channel === undefined) {
          routingResults[id] = {
            connection: result.connection.id,
            routingFound: false,
            routingWithLengthFound: false,
            channel: undefined
          }
          return
        }

        routingResults[id] = {
      connection: result.connection.id,
      routingFound: result.routingFound,
      routingWithLengthFound: result.routingWithLengthFound,
      channel: {
          path: {
            startPoint: channel.path.startPoint,
            endPoint: channel.path.endPoint,
            pathString: channel.path.pathString,
            length: channel.path.length,
            arcs: channel.path.arcs.map(a => {
              return {
                startPoint: a.startPoint,
                startDirection: a.startDirection,
                endPoint: a.endPoint,
                endDirection: a.endDirection,
                center: a.center,
                radius: a.radius,
                rotation: a.rotation,
                length: a.length
              }
            })
          },
          width: channel.width,
          length: channel.length
        }
      }
      })
      dispatch(updateChannels(routingResults))
    } catch(e) {
      dispatch(updateChannels({}))
      console.log(e)
    }
  }

  const clear = () => {
    const channels: Record<string, RoutingResult> = {}
    dispatch(updateChannels(channels))
  }

  return <Container fluid='lg' style={{
    maxWidth: '100%'
  }}>
    <Row>
    <Col xs={12} sm={12} md={12} lg={6} xl={6}>
    <Modal
      show={newConnectionEditor.active && !newConnectionEditor.settingsDone }
      onHide={() => {
        setNewConnectionEditor({
          active: false,
          settings: {},
          settingsDone: false,
          ports: [],
          firstPortDone: false,
        })
      }}
    >
      <Form>
      <Modal.Header closeButton>
        <Modal.Title
          className='connection'
        >
          New Connection
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group controlId="channelWidth">
          <Form.Label>Channel Width</Form.Label>
          <InputGroup>
          <Form.Control
            type="number"
            placeholder="100"
            value="100"
            onChange={(e) => {
                setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
                  draft.settings.width = Number.parseFloat(e.target.value)
                }))
              }
            }
          />
          <InputGroup.Append>
            <InputGroup.Text>µm</InputGroup.Text>
          </InputGroup.Append>
          </InputGroup>
          <Form.Text className="text-muted">
            The cross-section width of the microfluidic channel.
          </Form.Text>
        </Form.Group>

        <Form.Group controlId="channelSpacing">
          <Form.Label>Channel Spacing</Form.Label>
          <InputGroup>
          <Form.Control
            type="number"
            placeholder="50"
            value="50"
            onChange={(e) => {
                setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
                  draft.settings.spacing = Number.parseFloat(e.target.value)
                }))
              }
            }
          />
          <InputGroup.Append>
            <InputGroup.Text>µm</InputGroup.Text>
          </InputGroup.Append>
          </InputGroup>
          <Form.Text className="text-muted">
            The lateral area that must be clear of any obstacles.
          </Form.Text>
        </Form.Group>

        <Form.Group controlId="channelMinBendRadius">
          <Form.Label>Minimum Bend Radius</Form.Label>
          <InputGroup>
          <Form.Control
            type="number"
            placeholder="100"
            value="100"
            onChange={(e) => {
                setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
                  draft.settings.minBendRadius = Number.parseFloat(e.target.value)
                }))
              }
            }
          />
          <InputGroup.Append>
            <InputGroup.Text>µm</InputGroup.Text>
          </InputGroup.Append>
          </InputGroup>
          <Form.Text className="text-muted">
            The minimally allowed bend radius measured to the center of the channel.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-functional"
          onClick={() => {
            setNewConnectionEditor({
              active: false,
              settings: {},
              settingsDone: false,
              ports: [],
              firstPortDone: false
            })
          }}
        >
          <MdClose /> Discard
        </Button>
        <Button
          variant="connection"
          onClick={() => {
            const isSpecified = newConnectionEditor.settings.width !== undefined && newConnectionEditor.settings.spacing !== undefined && newConnectionEditor.settings.minBendRadius !== undefined
            const isValid = isSpecified && newConnectionEditor.settings.width !== Number.NaN && newConnectionEditor.settings.spacing !== Number.NaN && newConnectionEditor.settings.minBendRadius !== Number.NaN
            if(isValid) {
              setNewConnectionEditor(produce(newConnectionEditor, (draft) => {
                draft.settingsDone = true
              }))
            }
          }}
        >
          <MdDone /> Save changes
        </Button>
      </Modal.Footer>
      </Form>
    </Modal>

    <svg
      viewBox={viewBox} 
      ref={svg}
      {...gesture()}
      style={{
        touchAction: 'none',
        marginTop:"100px"
      }}
      className="editor"
      onClickCapture={clickSVG}
      onContextMenuCapture={rightClickSVG}
      onPointerMoveCapture={pointerMoveSVG}
      onPointerOutCapture={pointerOutSVG}
      width="100%"
      height="43em"
      //viewBox={`${view.viewBox.minx} ${view.viewBox.miny} ${view.viewBox.width} ${view.viewBox.height}`}
      //ref={svg}
      //onWheel={(e) => { scroll(e) }}
      onClick={(_) => { globals.selection.setSelected('') }}
    >
      {inputConfiguration.obstacles.map((obstacle) => {
        return <ObstacleSVGViewer
            key={obstacle.id}
            obstacle={obstacle}
            onChange={obstacle => dispatch(updateObstacle(obstacle))}
            globals={globals}
          />
      })}
      {inputConfiguration.connections.map((connection) => {
        return <ConnectionSVGViewer
            key={connection.id}
            connection={connection}
            onChange={connection => dispatch(updateConnection(connection))}
            strokeWidth={5}
            barriers={barriers}
            globals={globals}
          />
      })}
      { newObstacleEditor.active &&
        <polygon
          points={[...newObstacleEditor.points, newObstacleEditor.preview].map(p => p !== undefined ? `${p[0]},${p[1]}` : '').join(' ')}
          fill={settings.obstacleDefaultColor}
          style={{
            fill: settings.obstacleDefaultColor
          }}
        ></polygon>
      }
      { newObstacleEditor.active &&
        newObstacleEditor.points.map((point, i) => {
          return <circle
            className={i === 0 ? 'first-interactionpoint' : 'interactionpoint' }
            cx={point[0]}
            cy={point[1]}
            r={i === 0 ? settings.firstInteractionPointRadius / globals.view.scale : settings.interactionPointRadius / globals.view.scale}
          ></circle>
        })
      }
      { newObstacleEditor.active && newObstacleEditor.preview !== undefined &&
        <circle
          className='interactionpoint'
          cx={newObstacleEditor.preview[0]}
          cy={newObstacleEditor.preview[1]}
          r={settings.interactionPointRadius / globals.view.scale}
        ></circle>
      }
      { newConnectionEditor.active && newConnectionEditor.settingsDone && newConnectionEditor.ports.length >= 1 &&
        <PortSVGShape
          id={`addport_preview_0`}
          port={newConnectionEditor.ports[0]}
          portWidth={newConnectionEditor.settings.width!}
          colorScheme={shades(settings.connectionDefaultColor)}
          highlighted={false}
          preview={true}
        />
      }
      { newConnectionEditor.active && newConnectionEditor.settingsDone && newConnectionEditor.ports.length === 2 &&
        <PortSVGShape
          id={`addport_preview_1`}
          port={newConnectionEditor.ports[1]}
          portWidth={newConnectionEditor.settings.width!}
          colorScheme={shades(settings.connectionDefaultColor)}
          highlighted={false}
          preview={true}
        />
      }
    </svg>
    <span>
      x{view.scale.toFixed(3)}
      <Button
        variant="outline-functional"
        onClick={() => zoomIn()}
        style={{margin: '0.5em'}}
      >
        <AiOutlineZoomIn />
      </Button>
      <Button
        variant="outline-functional"
        onClick={() => zoomOut()}
        style={{margin: '0.5em'}}
      >
        <AiOutlineZoomOut />
      </Button>
      <Button
        variant="outline-functional"
        onClick={() => zoomToBounds()}
        style={{margin: '0.5em'}}
      >
        <MdCenterFocusStrong />
      </Button>
      <div
        style={{
          width: '12em',
          display: 'inline-block'
        }}
      >
        X: {view.pointerPosition !== undefined ? view.pointerPosition[0].toFixed(3) : '---.---'} Y: {view.pointerPosition !== undefined ? view.pointerPosition[1].toFixed(3) : '---.---'}
      </div>
    </span>
    </Col>
    <Col
      xs={12} sm={12} md={12} lg={6} xl={6}
    >
    <Button
      style={{
        margin: '1em'
      }}
      variant="functional"
      onClick={routeAndExtend}
    >
      Route
    </Button>

    <Button
      style={{
        margin: '1em'
      }}
      variant="outline-functional"
      onClick={clear}
    >
      Clear Routing
    </Button>

    <Button
      onClick={() => {
        dispatch(setInputConfiguration({
          obstacles: [],
          connections: []
        }))
      }}
      variant="outline-remove"
      style={{
        margin: '1em'
      }}
    >
      <RiDeleteBin2Line /> Remove all objects
    </Button>

    { !newObstacleEditor.active &&
      <Button
        variant="functional"
        onClick={() => {
          setNewObstacleEditor({
            active: true,
            points: [],
            preview: undefined,
            previewIsOnFirst: false
          })
        }}
        style={{
          margin: '1em'
        }}
      >
        <RiShapeLine /> Add Component
      </Button>
    }
    { newObstacleEditor.active &&
      <React.Fragment>
      <Button
        variant="outline-functional"
        onClick={() => {
          setNewObstacleEditor({
            active: false,
            points: [],
            preview: undefined,
            previewIsOnFirst: false
          })
        }}

        style={{
          margin: '1em'
        }}
      >
        <MdClose /> Discard
      </Button>
      <Button
        variant="functional"
        onClick={() => {
          if(newObstacleEditor.points.length > 0) {
            dispatch(addObstacle(newObstacleEditor.points))
          }
          setNewObstacleEditor({
            active: false,
            points: [],
            preview: undefined,
            previewIsOnFirst: false
          })
        }}
        style={{
          margin: '1em'
        }}
      >
        <MdDone /> Done
      </Button>
      </React.Fragment>
    }

    { !newConnectionEditor.active &&
      <Button
        variant="functional"
        onClick={() => {
          setNewConnectionEditor({
            active: true,
            settings: {
              width: 100,
              spacing: 50,
              minBendRadius: 100
            },
            settingsDone: false,
            ports: [],
            firstPortDone: false
          })
        }}

        style={{
          margin: '1em'
        }}
      >
        <RiRouteLine /> Add Connection
      </Button>
    }

    <Download />
    <DownloadDXF />

      { s !== undefined && t === 'Connection' &&
        <ConnectionDetails
          key={s.id}
          connection={s as Connection}
          onChange={connection => dispatch(updateConnection(connection))}
          onDelete={() => dispatch(deleteConnection(s!.id as string))}
        />
      }

      { s !== undefined && t === 'Obstacle' &&
        <ObstacleDetails
          key={s.id}
          obstacle={s as Obstacle}
          onChange={obstacle => dispatch(updateObstacle(obstacle))}
          onDelete={() => dispatch(deleteObstacle(s!.id as string))}
        />
      }
      </Col>
    </Row>
    </Container>
}
